import type { FeatureFlags, UserPreferencesProvider } from '@atlaskit/editor-common/types';
import { type LoomPluginOptions, loomPlugin } from '@atlaskit/editor-plugin-loom';
import { accessibilityUtilsPlugin } from '@atlaskit/editor-plugin-accessibility-utils';
import { avatarGroupPlugin } from '@atlaskit/editor-plugin-avatar-group';
import { undoRedoPlugin } from '@atlaskit/editor-plugin-undo-redo';
import { batchAttributeUpdatesPlugin } from '@atlaskit/editor-plugin-batch-attribute-updates';
import { borderPlugin } from '@atlaskit/editor-plugin-border';
import { featureFlagsPlugin } from '@atlaskit/editor-plugin-feature-flags';
import { connectivityPlugin } from '@atlaskit/editor-plugin-connectivity';
import type { EditorViewModePluginConfig } from '@atlaskit/editor-plugin-editor-viewmode';
import { editorViewModePlugin } from '@atlaskit/editor-plugin-editor-viewmode';
import { editorViewModeEffectsPlugin } from '@atlaskit/editor-plugin-editor-viewmode-effects';
import { cardPlugin } from '@atlaskit/editor-plugin-card';
import { selectionMarkerPlugin } from '@atlaskit/editor-plugin-selection-marker';
import { codeBidiWarningPlugin } from '@atlaskit/editor-plugin-code-bidi-warning';
import { contentInsertionPlugin } from '@atlaskit/editor-plugin-content-insertion';
import { customAutoformatPlugin } from '@atlaskit/editor-plugin-custom-autoformat';
import { dataConsumerPlugin } from '@atlaskit/editor-plugin-data-consumer';
import { datePlugin } from '@atlaskit/editor-plugin-date';
import { emojiPlugin } from '@atlaskit/editor-plugin-emoji';
import { expandPlugin } from '@atlaskit/editor-plugin-expand';
import { scrollIntoViewPlugin } from '@atlaskit/editor-plugin-scroll-into-view';
import { gridPlugin } from '@atlaskit/editor-plugin-grid';
import { helpDialogPlugin } from '@atlaskit/editor-plugin-help-dialog';
import { insertBlockPlugin } from '@atlaskit/editor-plugin-insert-block';
import { listPlugin } from '@atlaskit/editor-plugin-list';
import { mediaPlugin } from '@atlaskit/editor-plugin-media';
import { mediaInsertPlugin } from '@atlaskit/editor-plugin-media-insert';
import type { MentionPluginOptions } from '@atlaskit/editor-plugin-mentions';
import { mentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import { historyPlugin } from '@atlaskit/editor-plugin-history';
import { panelPlugin } from '@atlaskit/editor-plugin-panel';
import { pasteOptionsToolbarPlugin } from '@atlaskit/editor-plugin-paste-options-toolbar';
import { rulePlugin } from '@atlaskit/editor-plugin-rule';
import { statusPlugin } from '@atlaskit/editor-plugin-status';
import { tablesPlugin } from '@atlaskit/editor-plugin-table';
import { pastePlugin } from '@atlaskit/editor-plugin-paste';
import { engagementPlatformPlugin } from '@atlaskit/editor-plugin-engagement-platform';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugin-better-type-history';
import { clipboardPlugin } from '@atlaskit/editor-plugin-clipboard';
import { textColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugin-toolbar-lists-indentation';
import { hyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink';
import { breakoutPlugin } from '@atlaskit/editor-plugin-breakout';
import { alignmentPlugin } from '@atlaskit/editor-plugin-alignment';
import { guidelinePlugin } from '@atlaskit/editor-plugin-guideline';
import { layoutPlugin } from '@atlaskit/editor-plugin-layout';
import { extensionPlugin } from '@atlaskit/editor-plugin-extension';
import { collabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import { tasksAndDecisionsPlugin } from '@atlaskit/editor-plugin-tasks-and-decisions';
import { annotationPlugin } from '@atlaskit/editor-plugin-annotation';
import { findReplacePlugin } from '@atlaskit/editor-plugin-find-replace';
import { analyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { focusPlugin } from '@atlaskit/editor-plugin-focus';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import { compositionPlugin } from '@atlaskit/editor-plugin-composition';
import { contextIdentifierPlugin } from '@atlaskit/editor-plugin-context-identifier';
import type { ScrollGutterPluginOptions } from '@atlaskit/editor-plugin-base';
import { basePlugin } from '@atlaskit/editor-plugin-base';
import { decorationsPlugin } from '@atlaskit/editor-plugin-decorations';
import { typeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead';
import { captionPlugin } from '@atlaskit/editor-plugin-caption';
import { fragmentPlugin } from '@atlaskit/editor-plugin-fragment';
import { indentationPlugin } from '@atlaskit/editor-plugin-indentation';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugin-clear-marks-on-empty-doc';
import { selectionToolbarPlugin } from '@atlaskit/editor-plugin-selection-toolbar';
import { textFormattingPlugin } from '@atlaskit/editor-plugin-text-formatting';
import { quickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { placeholderPlugin } from '@atlaskit/editor-plugin-placeholder';
import { submitEditorPlugin } from '@atlaskit/editor-plugin-submit-editor';
import { copyButtonPlugin } from '@atlaskit/editor-plugin-copy-button';
import { unsupportedContentPlugin } from '@atlaskit/editor-plugin-unsupported-content';
import { editorDisabledPlugin } from '@atlaskit/editor-plugin-editor-disabled';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugin-floating-toolbar';
import { selectionPlugin } from '@atlaskit/editor-plugin-selection';
import { codeBlockPlugin } from '@atlaskit/editor-plugin-code-block';
import { codeBlockAdvancedPlugin } from '@atlaskit/editor-plugin-code-block-advanced';
import { metricsPlugin } from '@atlaskit/editor-plugin-metrics';
import { primaryToolbarPlugin } from '@atlaskit/editor-plugin-primary-toolbar';
import { blockTypePlugin } from '@atlaskit/editor-plugin-block-type';
import { widthPlugin } from '@atlaskit/editor-plugin-width';
import { ufoPlugin } from '@atlaskit/editor-plugin-ufo';
import { shouldForceTracking } from '@atlaskit/editor-common/utils';
import {
	contextPanelPlugin,
	type ContextPanelPluginOptions,
} from '@atlaskit/editor-plugin-context-panel';
import { highlightPlugin } from '@atlaskit/editor-plugin-highlight';
import { blockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import { placeholderTextPlugin } from '@atlaskit/editor-plugin-placeholder-text';
import type { EngagementPlatformPluginConfig } from '@atlaskit/editor-plugin-engagement-platform';
import { selectionExtensionPlugin } from '@atlaskit/editor-plugin-selection-extension';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { referentialityPlugin } from '@atlassian/editor-referentiality';
import { forgeEditorPlugin } from '@atlassian/xen-editor-provider';
import type { AIDefinitionsPluginConfig } from '@atlassian/editor-plugin-ai-definitions';
import { aiDefinitionsPlugin } from '@atlassian/editor-plugin-ai-definitions';
import {
	aiProactivePlugin,
	type AIProactivePluginConfig,
} from '@atlassian/editor-plugin-ai-proactive';
import { aiPlugin } from '@atlassian/editor-plugin-ai';
import type { createPageEditorPluginAIOptions } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';

import { EditorHeadingLinksPlugin } from '@confluence/editor-heading-links';
import { editorOfflineStoragePlugin } from '@confluence/editor-offline-storage';
import { fg } from '@confluence/feature-gating';
import type { AIGlobalOptIn } from '@confluence/editor-features';
import type { FullPageAppearances } from '@confluence/content-appearance';
import { fabricExtensionManagerPlugin } from '@confluence/fabric-extension-manager-plugin';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { legacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import {
	createInlineCommentCompoundExperience,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker';

import { createFeatureFlagsFromProps } from '../utils/createFeatureFlagsFromProps';

import { pageTitleUtilsPlugin } from './plugins/pageTitleUtilsPlugin';
import { ssrPlugin } from './plugins/ssrPlugin';
import { emptyStatePlugin } from './plugins/emptyStatePlugin';
import type { FullPageEditorPresetProps } from './useFullPageEditorPreset';
import { conflictResolutionUIPlugin } from './plugins/conflictResolutionUIPlugin';

export interface FullPageEditorPresetOptions extends FullPageEditorPresetProps {
	prevAppearance?: FullPageAppearances;
	editorPluginAIOptions: ReturnType<typeof createPageEditorPluginAIOptions>;
	aiGlobalOptIn: {
		status: AIGlobalOptIn['optInStatus'];
		triggerOptInFlow: AIGlobalOptIn['triggerOptInFlow'];
	};
	loomPluginOptions: LoomPluginOptions;
	isLoomPluginEnabled: boolean;
	textHighlightingFloatingToolbarExperiment: boolean;
	handleMentionsChanged: MentionPluginOptions['handleMentionsChanged'];
	editorPluginAIDefinitionsConfig: AIDefinitionsPluginConfig;
	editorPluginAIDefinitionsEnabled: boolean;
	editorPluginAIProactiveEnabled: boolean;
	editorPluginAIProactiveConfig: AIProactivePluginConfig;
	editorEngagementPlatformPluginConfig: EngagementPlatformPluginConfig;
	isDesktop: boolean;
	isHeadingLinksEnabled: boolean;
	initialPluginConfiguration: {
		viewModePlugin: EditorViewModePluginConfig;
	};
	// Make below option mandatory after cleaning platform_editor_ai_object_sidebar_injection
	contextPanelOptions?: ContextPanelPluginOptions;
	removeScrollGutter?: boolean;
	userPreferencesProvider?: UserPreferencesProvider;
}

/**
 * Creates a preset with plugins for the comment editor.
 *
 * @param props A subset of full EditorProps for the full feature preset
 * @returns a full featured preset configured according to the provided props - basis for create-plugins-list
 */
// FIXME: https://product-fabric.atlassian.net/browse/ED-25722
export function createFullPageEditorPreset(options: FullPageEditorPresetOptions): any {
	const appearance = options.appearance;
	const prevAppearance = options.prevAppearance;
	const featureFlags: FeatureFlags = createFeatureFlagsFromProps(options.featureFlags);
	const cardOptions = options.linking?.smartLinks;
	const getEditorFeatureFlags = () => featureFlags;

	// Note -- when cleaning up these feature flags -- the object being created here should be moved back to inline

	// originally have feedbackDialogPlugin, saveOnEnterPlugin, imageUploadPlugin, legacyImageUploadProvider, maxContentSizePlugin, beforePrimaryToolbarPlugin from universal preset
	// no longer needed in full page preset
	return (
		new EditorPresetBuilder()
			.add([featureFlagsPlugin, featureFlags || {}])
			.add([analyticsPlugin, {}])
			.add(betterTypeHistoryPlugin)
			.add([pastePlugin, { cardOptions, sanitizePrivateContent: true, isFullPage: true }])
			.add(clipboardPlugin)
			.add(focusPlugin)
			.add(compositionPlugin)
			.add([
				contextIdentifierPlugin,
				{ contextIdentifierProvider: options.contextIdentifierProvider },
			])
			.add([
				basePlugin,
				// originally come from packages/editor/editor-core/src/create-editor/create-plugins-list.ts
				{
					allowInlineCursorTarget: true,
					allowScrollGutter: options.removeScrollGutter
						? undefined
						: ({
								getScrollElement: () =>
									document.querySelector('.fabric-editor-popup-scroll-parent'),
							} as ScrollGutterPluginOptions),
				},
			])
			.add(decorationsPlugin)
			.add([
				typeAheadPlugin,
				{
					isMobile: false,
				},
			])
			// originally depends on Boolean(options.allowUndoRedoButtons)
			.add(historyPlugin)
			.add([primaryToolbarPlugin, { contextualFormattingEnabled: true }])
			// originally depends on Boolean(options.featureFlags?.undoRedoButtons ?? options.allowUndoRedoButtons)
			.add(undoRedoPlugin)
			.add([
				blockTypePlugin,
				{
					includeBlockQuoteAsTextstyleOption: true,
					lastNodeMustBeParagraph: false,
					// originally depends on allowUndoRedoButtons
					isUndoRedoButtonsEnabled: true,
					// no allowBlockType set in FullPageEditorComponent
					allowBlockType: undefined,
				},
			])
			.add(clearMarksOnEmptyDocPlugin)
			.add([
				selectionToolbarPlugin,
				{
					preferenceToolbarAboveSelection: false,
					contextualFormattingEnabled: true,
					userPreferencesProvider: options.userPreferencesProvider,
				},
			])
			.add([
				hyperlinkPlugin,
				{
					editorAppearance: appearance,
					linkPicker: options.linking?.linkPicker,
					lpLinkPicker: featureFlags.lpLinkPicker ?? false,
					onClickCallback: cardOptions?.onClickCallback,
					platform: 'web',
				},
			])
			.add([
				textFormattingPlugin,
				{
					responsiveToolbarMenu: true,
				},
			])
			.add(widthPlugin)
			.add([
				quickInsertPlugin,
				{
					enableElementBrowser: options.elementBrowser?.showModal,
					elementBrowserHelpUrl: options.elementBrowser?.helpUrl,
					disableDefaultItems: false,
					headless: false,
					emptyStateHandler: options.elementBrowser?.emptyStateHandler,
					prioritySortingFn:
						typeof options.quickInsert === 'object'
							? options.quickInsert.prioritySortingFn
							: undefined,
					onInsert:
						typeof options.quickInsert === 'object' ? options.quickInsert.onInsert : undefined,
				},
			])
			.add([
				placeholderPlugin,
				{
					placeholder: options.placeholder,
				},
			])
			.add(unsupportedContentPlugin)
			.add(editorDisabledPlugin)
			.add([submitEditorPlugin, options.onSave])
			.add(copyButtonPlugin)
			.add(floatingToolbarPlugin)
			.add([selectionPlugin, { useLongPressSelection: false, __livePage: options.__livePage }])
			.add([
				codeBlockPlugin,
				{
					allowCopyToClipboard: true,
					useLongPressSelection: false,
					allowCompositionInputOverride: false,
				},
			])
			// above were refactored from createDefaultPreset
			.add(ufoPlugin)
			.add(dataConsumerPlugin)
			.add(accessibilityUtilsPlugin)
			.add(contentInsertionPlugin)
			.add(batchAttributeUpdatesPlugin)
			.add([breakoutPlugin, { allowBreakoutButton: appearance === 'full-page' }])
			.add(alignmentPlugin)
			.add([textColorPlugin, true])
			.add(listPlugin)
			.add(rulePlugin)
			.add([
				expandPlugin,
				{
					allowInsertion: true,
					useLongPressSelection: false,
					appearance,
					allowInteractiveExpand: true,
					__livePage: options.__livePage,
				},
			])
			.add(guidelinePlugin)
			// originally depends on Boolean(options.media), change to .add as FullPageEditorComponent has set media options
			.add([gridPlugin, { shouldCalcBreakoutGridLines: true }])
			// originally depends on Boolean(options.annotationProviders), change to .add as FullPageEditorComponent has set annotationProviders
			.add([
				annotationPlugin,
				fg('confluence_comments_select_comment_experience')
					? options.annotationProviders
						? {
								...options.annotationProviders,
								createCommentExperience: createInlineCommentCompoundExperience,
								selectCommentExperience: {
									selectAnnotation: {
										complete: (annotationId: string) => {
											selectInlineCommentCompoundExperience.selectAnnotation.complete({
												pageClass: 'editor',
												annotationId,
												entryPoint: 'annotation-change',
											});
										},
									},
								},
							}
						: undefined
					: options.annotationProviders
						? {
								inlineComment: options.annotationProviders.inlineComment,
								createCommentExperience: createInlineCommentCompoundExperience,
							}
						: undefined,
			])
			// originally depends on Boolean(options.media), change to .add as FullPageEditorComponent has set media options
			.add([
				mediaPlugin,
				{
					...options.media, // values are from next/packages/editor-features/src/utils/mediaOptions.ts
					// below values are copied from legacy preset to avoid regression
					allowLazyLoading: true,
					allowBreakoutSnapPoints: true,
					allowAdvancedToolBarOptions:
						typeof options.media?.allowAdvancedToolBarOptions !== 'undefined'
							? options.media?.allowAdvancedToolBarOptions
							: true,
					// originally value was Boolean(options.annotationProviders),
					allowCommentsOnMedia: true,
					allowDropzoneDropLine: true,
					allowMediaSingleEditable: true,
					allowRemoteDimensionsFetch: true,
					allowMarkingUploadsAsIncomplete: false,
					allowImagePreview: true,
					fullWidthEnabled: appearance === 'full-width',
					editorAppearance: appearance,
					waitForMediaUpload: options.waitForMediaUpload,
					isCopyPasteEnabled: true,
					alignLeftOnInsert:
						typeof options.media?.alignLeftOnInsert !== 'undefined'
							? options.media?.alignLeftOnInsert
							: false,
					getEditorFeatureFlags,
				},
			])
			// original value was Boolean(options.media), change to .add as FullPageEditorComponent has set media options
			.add(mediaInsertPlugin)
			// original value was Boolean(options.media), change to .add as FullPageEditorComponent has set media options
			.add(captionPlugin)
			.add([
				mentionsPlugin,
				{
					sanitizePrivateContent: true,
					insertDisplayName: true,
					allowZeroWidthSpaceAfter: true,
					profilecardProvider: options.mention?.profilecardProvider,
					handleMentionsChanged: options.handleMentionsChanged,
					mentionProvider: options.mentionProvider,
				},
			])
			// original value was Boolean(options.emojiProvider), change to .add as FullPageEditorComponent has set emojiProvider
			.add([
				emojiPlugin,
				{
					emojiProvider: options.emojiProvider,
				},
			])
			.add([
				tablesPlugin,
				{
					// value from next/packages/editor-features/src/hooks/useTableOptions.ts
					tableOptions: {
						allowTableResizing: true,
						allowTableAlignment: true,
						allowBackgroundColor: true,
						allowColumnResizing: true,
						allowColumnSorting: true,
						allowDistributeColumns: true,
						allowHeaderColumn: true,
						allowHeaderRow: true,
						allowMergeCells: true,
						allowNumberColumn: true,
						allowNestedTables: true,
						allowControls: true,
						stickyHeaders: true,
						allowAddColumnWithCustomStep: false,
					},
					dragAndDropEnabled: featureFlags?.tableDragAndDrop,
					isTableScalingEnabled: true,
					allowContextualMenu: true,
					fullWidthEnabled: appearance === 'full-width',
					wasFullWidthEnabled: prevAppearance && prevAppearance === 'full-width',
					getEditorFeatureFlags,
					isCommentEditor: false,
					isChromelessEditor: false,
				},
			])
			.add([
				tasksAndDecisionsPlugin,
				{
					allowNestedTasks: true,
					consumeTabs: true,
					useLongPressSelection: false,
					taskDecisionProvider: options.taskDecisionProvider,
					hasEditPermission: options.hasEditPermission,
					requestToEditContent: options.requestToEditContent,
					hasRequestedEditPermission: options.hasRequestedEditPermission,
				},
			])
			.add([helpDialogPlugin, false])
			.add([
				collabEditPlugin,
				{
					...options.collabEdit,
					sanitizePrivateContent: true,
					EXPERIMENTAL_allowInternalErrorAnalytics:
						options.collabEdit?.EXPERIMENTAL_allowInternalErrorAnalytics ?? shouldForceTracking(),
					hideTelecursorOnLoad:
						options.__livePage && fg('platform_editor_no_telecursor_on_live_doc_init'),
				},
			])
			.add([
				panelPlugin,
				{
					useLongPressSelection: false,
					// next/packages/full-page-editor/src/FullPageEditorComponent.tsx
					allowCustomPanel: true,
					allowCustomPanelEdit: true,
				},
			])
			.add([
				contextPanelPlugin,
				fg('platform_editor_ai_object_sidebar_injection') ? options.contextPanelOptions : undefined,
			])
			.add(
				[
					extensionPlugin,
					{
						breakoutEnabled: appearance === 'full-page',
						extensionHandlers: options.extensionHandlers,
						useLongPressSelection: false,
						appearance,
						__rendererExtensionOptions: options.__rendererExtensionOptions,
					},
				],
				// extensionOptions available in FullPageEditorComponent
				// Boolean(options.allowExtension),
			)
			.add([
				datePlugin,
				{
					weekStartDay: undefined,
				},
			])
			.add([placeholderTextPlugin, {}])
			.add([
				layoutPlugin,
				{
					// next/packages/editor-features/src/utils/layoutOptions.ts
					allowBreakout: true,
					UNSAFE_addSidebarLayouts: true,
					useLongPressSelection: false,
					UNSAFE_allowSingleColumnLayout: undefined,
					editorAppearance: appearance,
				},
			])
			.add([
				cardPlugin,
				{
					...options.linking?.smartLinks,
					fullWidthMode: appearance === 'full-width',
					linkPicker: options.linking?.linkPicker,
					lpLinkPicker: featureFlags.lpLinkPicker ?? false,
					editorAppearance: appearance,
					// @ts-ignore Temporary solution to check for Live Page editor.
					__livePage: options.__livePage,
				},
			])
			.add([
				customAutoformatPlugin,
				{
					autoformattingProvider: options.autoformattingProvider,
				},
			])
			.add([
				statusPlugin,
				{
					menuDisabled: false,
					allowZeroWidthSpaceAfter: true,
				},
			])
			.add(indentationPlugin)
			.add(scrollIntoViewPlugin)
			.add([
				toolbarListsIndentationPlugin,
				{
					showIndentationButtons: true,
					allowHeadingAndParagraphIndentation: true,
				},
			])
			.add([
				insertBlockPlugin,
				{
					allowTables: true,
					allowExpand: true,
					// insertMenuItems: options.insertMenuItems,
					horizontalRuleEnabled: true,
					tableSelectorSupported: featureFlags?.tableSelector,
					nativeStatusSupported: true,
					showElementBrowserLink: true,
					appearance,
				},
			])
			.add([
				avatarGroupPlugin,
				{
					// Avatars are moved to Confluence codebase for Edit in Context
					// When Edit in Context is enabled primaryToolbarComponents is undefined
					// For more details please check
					// https://hello.atlassian.net/wiki/spaces/PCG/pages/2851572180/Editor+toolbar+for+live+pages+and+edit+in+context+projects
					collabEdit: options.collabEdit,
					// originally depends on !hasBeforePrimaryToolbar(props.primaryToolbarComponents),
					takeFullWidth: true,
					showAvatarGroup: false,
				},
			])
			.maybeAdd(
				[
					findReplacePlugin,
					{
						// originally depends on !!featureFlags.showAvatarGroupAsPlugin === false && !hasBeforePrimaryToolbar(props.primaryToolbarComponents),
						takeFullWidth: true,
						twoLineEditorToolbar: false,
					},
				],
				// controled by !collabEditInternetLost && findReplaceOptions in FullPageEditorComponent
				Boolean(options.allowFindReplace),
			)
			.add(borderPlugin)
			.add(fragmentPlugin)
			.add(pasteOptionsToolbarPlugin)
			.add([
				codeBidiWarningPlugin,
				{
					appearance,
				},
			])
			// below plugins moved from legacy preset
			.maybeAdd(
				[
					referentialityPlugin,
					{
						...options.referentialityContext.options,
						api: options.referentialityContext.api ?? undefined,
					},
				],
				Boolean(options.referentialityContext.plugin),
			)
			.maybeAdd(
				[aiPlugin, options.editorPluginAIOptions],
				Boolean(options.editorPluginAIOptions.aiGlobalOptIn.status !== 'disabled'),
			)
			.maybeAdd([loomPlugin, options.loomPluginOptions], options.isLoomPluginEnabled)
			.add([
				editorViewModePlugin,
				editorExperiment('live_pages_graceful_edit', 'control')
					? { mode: options.viewMode }
					: options.initialPluginConfiguration.viewModePlugin,
			])
			.add(editorViewModeEffectsPlugin)
			// aiDefinitionsPlugin depends on editorViewModePlugin
			.maybeAdd(
				[aiDefinitionsPlugin, options.editorPluginAIDefinitionsConfig],
				fg('confluence_ai_access_policy') ? true : options.editorPluginAIDefinitionsEnabled,
			)
			// aiProactivePlugin depends on aiPlugin
			.maybeAdd(
				[aiProactivePlugin, options.editorPluginAIProactiveConfig],
				options.editorPluginAIProactiveEnabled,
			)
			.add([
				selectionMarkerPlugin,
				fg('platform_editor_no_cursor_on_live_doc_init')
					? { hideCursorOnInit: options.__livePage }
					: undefined,
			])
			.add(ssrPlugin)
			.add(blockControlsPlugin)
			.maybeAdd(
				[
					EditorHeadingLinksPlugin,
					{
						getViewPagePath: options.getViewPagePath,
					},
				],
				options.isHeadingLinksEnabled,
			)
			.add([
				highlightPlugin,
				{
					textHighlightingFloatingToolbarExperiment:
						options.textHighlightingFloatingToolbarExperiment,
				},
			])
			.add(pageTitleUtilsPlugin)
			.add(fabricExtensionManagerPlugin)
			.add([engagementPlatformPlugin, options.editorEngagementPlatformPluginConfig])
			.add(forgeEditorPlugin)
			.maybeAdd(
				connectivityPlugin,
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			)
			.maybeAdd(metricsPlugin, fg('platform_editor_ease_of_use_metrics'))
			.maybeAdd(
				editorOfflineStoragePlugin,
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			)
			.maybeAdd(
				conflictResolutionUIPlugin,
				options.isDesktop || expVal('platform_editor_offline_editing_web', 'isEnabled', false),
			)
			.add(emptyStatePlugin)
			.maybeAdd(
				codeBlockAdvancedPlugin,
				expVal('platform_editor_advanced_code_blocks', 'isEnabled', false),
			)
			.maybeAdd(legacyContentMacroPlugin, fg('platform_editor_legacy_content_macro'))
			.maybeAdd(
				[
					selectionExtensionPlugin,
					{
						extensions: {},
					},
				],
				expValEquals('platform_editor_controls', 'cohort', 'variant1'),
			)
	);
}
